import React, { useState, useEffect } from 'react';
import { Login } from './components/Auth/Login';
import {
  MainLayout,
  type AppTab,
  type DatasetType,
  type Project,
} from './components/Layout/MainLayout';
import { ChatBot } from './components/ChatBot/ChatBot';
import { MarimoFrame } from './components/MarimoConsole/MarimoFrame.tsx';
import { Insights } from './components/Insights/Insights';
import { Maps } from './components/Maps/Maps';
import { useChat } from './hooks/useChat';
import { useApiHealth } from './hooks/useMarimoSession';
import { DEMO_USERS, DEFAULT_VIEW_AS_KEY, getDemoUser } from './data/users';

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isConsoleOpen, setIsConsoleOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<AppTab>('copilot');

  // "View as" demo persona — defaults to DOE TTO. Switching this resets
  // project + dataset type to the first valid pair for the new user.
  const [viewAsKey, setViewAsKey] = useState<string>(DEFAULT_VIEW_AS_KEY);
  const activeUser = getDemoUser(viewAsKey);
  const [project, setProject] = useState<Project>(activeUser.projects[0]?.key ?? '');
  const [datasetType, setDatasetType] = useState<DatasetType>(
    activeUser.projects[0]?.types[0]?.key ?? ''
  );

  const handleViewAsChange = (nextKey: string) => {
    const next = getDemoUser(nextKey);
    setViewAsKey(nextKey);
    const firstProject = next.projects[0];
    setProject(firstProject?.key ?? '');
    setDatasetType(firstProject?.types[0]?.key ?? '');
    // Keep the backend username in sync for /api/me / /api/session calls.
    localStorage.setItem('agentic.username', next.username);
  };

  const {
    messages,
    isLoading,
    sendMessage,
    clearMessages,
    clearMessagesAndShowWelcome,
    showWelcomeGreeting,
  } = useChat();
  const { isConnected, agents } = useApiHealth();

  // Check authentication status on mount
  useEffect(() => {
    const authStatus = localStorage.getItem('isAuthenticated');
    setIsAuthenticated(authStatus === 'true');
  }, []);

  const handleLogin = () => {
    showWelcomeGreeting();
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    setIsAuthenticated(false);
    clearMessages();
  };

  const handleSendMessage = async (message: string) => {
    await sendMessage(message);
  };

  const handleProfile = () => {
    alert('User profile management coming soon.');
  };

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <MainLayout
      isConnected={isConnected}
      agents={agents}
      onClearChat={clearMessagesAndShowWelcome}
      onLogout={handleLogout}
      onProfile={handleProfile}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      project={project}
      onProjectChange={setProject}
      datasetType={datasetType}
      onDatasetTypeChange={setDatasetType}
      viewAsUsers={DEMO_USERS}
      viewAsKey={viewAsKey}
      onViewAsChange={handleViewAsChange}
      activeUser={activeUser}
    >
      {activeTab === 'copilot' && (
        <div style={styles.workspace}>
          <div style={styles.chatSection}>
            <ChatBot
              messages={messages}
              isLoading={isLoading}
              onSendMessage={handleSendMessage}
            />
          </div>
          <button
            onClick={() => setIsConsoleOpen(o => !o)}
            style={styles.toggleTab}
            title={isConsoleOpen ? 'Collapse Python Console' : 'Expand Python Console'}
          >
            <span style={styles.toggleLabel}>Python Console</span>
            <span style={styles.toggleArrow}>{isConsoleOpen ? '▶' : '◀'}</span>
          </button>
          <div style={{ ...styles.consoleSection, width: isConsoleOpen ? '55%' : 0 }}>
            <MarimoFrame />
          </div>
        </div>
      )}
      {activeTab === 'insights' && (
        <Insights project={project} datasetType={datasetType} />
      )}
      {activeTab === 'maps' && <Maps viewAsKey={viewAsKey} datasetType={datasetType} />}
    </MainLayout>
  );
};

const styles: Record<string, React.CSSProperties> = {
  workspace: {
    display: 'flex',
    height: '100%',
    overflow: 'hidden',
  },
  chatSection: {
    flex: 1,
    borderRight: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  toggleTab: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    width: 24,
    flexShrink: 0,
    background: '#f1f5f9',
    border: 'none',
    borderLeft: '1px solid #e2e8f0',
    borderRight: '1px solid #e2e8f0',
    cursor: 'pointer',
    padding: '8px 0',
    color: '#475569',
    fontSize: '0.65rem',
    userSelect: 'none',
  },
  toggleLabel: {
    writingMode: 'vertical-rl',
    textOrientation: 'mixed',
    transform: 'rotate(180deg)',
    fontWeight: 600,
    letterSpacing: '0.05em',
    fontSize: '0.65rem',
  },
  toggleArrow: {
    fontSize: '0.6rem',
  },
  consoleSection: {
    flexShrink: 0,
    overflow: 'hidden',
    transition: 'width 0.25s ease',
    display: 'flex',
    flexDirection: 'column',
  },
};

export default App;
